import React from "react";
import { PageHero, Section, Eyebrow, SectionTitle } from "../components/Primitives";

const temple2 = "https://images.unsplash.com/photo-1589369125429-cad824743af3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxidWRkaGlzdCUyMHRlbXBsZXxlbnwwfHx8fDE3ODgyNDUxMTR8MA&ixlib=rb-4.1.0&q=85";

const milestones = [
  { y: "1983", t: "A humble beginning", d: "A small group of Vietnamese refugees and Canberran friends gather in a rented hall to chant, share food and remember home. Van Hanh is born." },
  { y: "1994", t: "Our home in Lyneham", d: "After a decade of shared meals and shared savings, the community purchases 32 Archibald Street and begins building the monastery grounds by hand." },
  { y: "2003", t: "Doors widen", d: "Meditation and dharma classes are offered in English for the first time. The library opens with 300 donated titles." },
  { y: "2012", t: "Rahula Lodge", d: "The Rahula Community Lodge is founded to offer safe, affordable accommodation to travellers, students and those in transition." },
  { y: "2019", t: "Registered charity", d: "The United Vietnamese Buddhist Congregation of Canberra formally registers with the ACNC to strengthen its community work." },
  { y: "Today", t: "A home for all", d: "Forty years on, more than a thousand people pass through our gates each month for practice, learning, shelter and belonging." },
];

const About = () => (
  <>
    <PageHero
      eyebrow="Our Story"
      title="Forty years of quiet, patient service."
      subtitle="Van Hanh Monastery grew from the shared hopes of a small Vietnamese community in Canberra. Today it belongs to everyone who finds shelter here."
    />

    <Section>
      <div className="grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-6">
          <div className="relative aspect-[4/5] overflow-hidden">
            <img src={temple2} alt="Detail of traditional temple architecture" className="w-full h-full object-cover" loading="lazy" />
          </div>
        </div>
        <div className="lg:col-span-6">
          <Eyebrow>Who we are</Eyebrow>
          <SectionTitle>A registered charity. A living community.</SectionTitle>
          <div className="mt-6 space-y-5 text-[17px] text-[#4a3a2a] leading-relaxed">
            <p>The United Vietnamese Buddhist Congregation of Canberra and Surrounds is a medium-sized registered charity (ABN 20 648 948 015) operating from Van Hanh Monastery in Lyneham, ACT.</p>
            <p>Our purposes are simple: to advance religion through the practice of Buddhism, to relieve poverty and hardship, to support affordable housing, and to build a compassionate multicultural community in Canberra and beyond.</p>
            <p>We are led by resident monastics, guided by a lay council, and sustained by hundreds of volunteers of every background and belief.</p>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-6 text-sm">
            <div><div className="uppercase tracking-widest text-xs text-[#8a6b3f]">Established</div><div className="font-serif text-2xl text-[#3d2817] mt-1">31 December 1983</div></div>
            <div><div className="uppercase tracking-widest text-xs text-[#8a6b3f]">Charity size</div><div className="font-serif text-2xl text-[#3d2817] mt-1">Medium</div></div>
            <div><div className="uppercase tracking-widest text-xs text-[#8a6b3f]">Head office</div><div className="font-serif text-2xl text-[#3d2817] mt-1">Lyneham, ACT</div></div>
            <div><div className="uppercase tracking-widest text-xs text-[#8a6b3f]">We operate in</div><div className="font-serif text-2xl text-[#3d2817] mt-1">AU · VN · IN · NP</div></div>
          </div>
        </div>
      </div>
    </Section>

    {/* Mission */}
    <section className="bg-[#f6efe0] border-y border-[#e9dcc0]">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24 text-center">
        <Eyebrow>Our mission</Eyebrow>
        <p className="font-serif text-3xl md:text-4xl text-[#3d2817] leading-[1.25] mt-6">
          To offer refuge, learning and kindness — to nourish body and mind — and to build a Canberra where no one feels alone.
        </p>
      </div>
    </section>

    {/* Timeline */}
    <Section>
      <div className="text-center mb-16">
        <Eyebrow>Milestones</Eyebrow>
        <SectionTitle className="mt-6">A slow, patient story.</SectionTitle>
      </div>
      <ol className="relative border-l border-[#e9dcc0] max-w-3xl mx-auto">
        {milestones.map((m) => (
          <li key={m.y} className="pl-8 pb-12 relative">
            <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-[#7a2e1f] ring-4 ring-[#fdfaf3]" />
            <div className="font-serif text-3xl text-[#c9a15b]">{m.y}</div>
            <h3 className="font-serif text-2xl text-[#3d2817] mt-1">{m.t}</h3>
            <p className="mt-3 text-[#5a4a3a] leading-relaxed">{m.d}</p>
          </li>
        ))}
      </ol>
    </Section>

    {/* Values */}
    <section className="bg-[#fdfaf3] border-t border-[#e9dcc0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center mb-14">
          <Eyebrow>Our values</Eyebrow>
          <SectionTitle className="mt-6">Four things we try to practise, imperfectly.</SectionTitle>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { t: "Compassion", d: "To meet suffering with warmth, not judgement — in ourselves and in others." },
            { t: "Simplicity", d: "To live and give with less, so more can be shared with those who need it." },
            { t: "Belonging", d: "To make room for every language, background, orientation and story." },
            { t: "Stewardship", d: "To hold our resources, land and community with quiet, careful hands." },
          ].map((v) => (
            <div key={v.t} className="p-8 border border-[#e9dcc0] bg-[#f6efe0]/60">
              <h3 className="font-serif text-2xl text-[#3d2817]">{v.t}</h3>
              <p className="mt-3 text-[#5a4a3a] leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  </>
);

export default About;
